import React, { useEffect, useRef, useState } from "react";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { useNavigate } from "react-router-dom";
import { useExchangePosOauthMutation } from "../../../../Api/attoDeskApi";
import { NAVY, TEXT_SECONDARY, SUCCESS, ERROR } from "../../../../theme/tokens";

const POS_TEAL = "#00838f";
const RETURN_KEY = "pos_oauth_return";

/**
 * Clover redirects the browser here (CLOVER_REDIRECT_URI) with ?code&merchant_id
 * &state. We POST them to the Sanctum-protected exchange so the token swap stays
 * authenticated, then return the user to the playlist they started from.
 */
const PosOauthCallback: React.FC = () => {
  const navigate = useNavigate();
  const [exchange] = useExchangePosOauthMutation();
  const [phase, setPhase] = useState<"working" | "done" | "error">("working");
  const [message, setMessage] = useState("Finishing the Clover connection…");
  const ran = useRef(false);

  const returnTo = (() => {
    try { return sessionStorage.getItem(RETURN_KEY) || "/playlist/list"; }
    catch { return "/playlist/list"; }
  })();

  useEffect(() => {
    if (ran.current) return; // guard React 18 double-invoke
    ran.current = true;

    const params = new URLSearchParams(window.location.search);
    const error = params.get("error");
    const code = params.get("code");
    const merchantId = params.get("merchant_id");
    const state = params.get("state");

    if (error) {
      setPhase("error");
      setMessage("Clover authorization was cancelled or denied.");
      return;
    }
    if (!code || !merchantId || !state) {
      setPhase("error");
      setMessage("The Clover response was missing required parameters.");
      return;
    }

    exchange({ code, merchant_id: merchantId, state })
      .unwrap()
      .then(() => {
        setPhase("done");
        setMessage("Clover connected. Returning to your menu…");
        setTimeout(() => navigate(returnTo, { replace: true }), 1200);
      })
      .catch((e: any) => {
        setPhase("error");
        setMessage(e?.data?.message || "Could not complete the Clover connection.");
      });
  }, [exchange, navigate, returnTo]);

  return (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "70vh", p: 3 }}>
      <Box sx={{ textAlign: "center", maxWidth: 420 }}>
        {phase === "working" && <CircularProgress sx={{ color: POS_TEAL, mb: 2 }} />}
        {phase === "done" && <CheckCircleIcon sx={{ fontSize: 48, color: SUCCESS, mb: 1 }} />}
        {phase === "error" && <ErrorOutlineIcon sx={{ fontSize: 48, color: ERROR, mb: 1 }} />}
        <Typography sx={{ fontWeight: 800, color: NAVY, mb: 0.5 }}>
          {phase === "error" ? "Connection failed" : "Connecting Clover"}
        </Typography>
        <Typography sx={{ fontSize: 13, color: TEXT_SECONDARY, mb: 2 }}>{message}</Typography>
        {phase === "error" && (
          <Button variant="outlined" onClick={() => navigate(returnTo, { replace: true })}
            sx={{ textTransform: "none", fontWeight: 700 }}>
            Back to menu
          </Button>
        )}
      </Box>
    </Box>
  );
};

export default PosOauthCallback;
